import React, { useState, useEffect, useRef } from "react";
import { apiClient } from "../utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Plus,
  Pencil,
  Trash2,
  AlertTriangle,
  Clock,
  CalendarDays,
  FileText,
  ChevronRight,
  Hash,
  StickyNote,
  Link as LinkIcon,
  X,
  Share2,
  Copy,
  ExternalLink,
  SendHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { DateTimePicker } from "@/components/ui/date-time-picker";

// ── Constants ──────────────────────────────────────────────────────────────

const COLUMNS = [
  {
    key: "draft",
    label: "Draft",
    next: "approved",
    nextLabel: "Mark Approved",
    dot: "bg-slate-400",
    header: "bg-slate-50 border-slate-200 text-slate-700",
    badge: "bg-slate-50 text-slate-600 border-slate-200",
  },
  {
    key: "approved",
    label: "Approved",
    next: "scheduled",
    nextLabel: "Schedule",
    dot: "bg-emerald-500",
    header: "bg-emerald-50 border-emerald-200 text-emerald-700",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  {
    key: "declined",
    label: "Declined",
    next: "draft",
    nextLabel: "Back to Draft",
    dot: "bg-rose-500",
    header: "bg-rose-50 border-rose-200 text-rose-700",
    badge: "bg-rose-50 text-rose-700 border-rose-200",
  },
  {
    key: "scheduled",
    label: "Scheduled",
    next: "posted",
    nextLabel: "Mark Posted",
    dot: "bg-violet-500",
    header: "bg-violet-50 border-violet-200 text-violet-700",
    badge: "bg-violet-50 text-violet-700 border-violet-200",
  },
  {
    key: "posted",
    label: "Posted",
    next: null,
    nextLabel: null,
    dot: "bg-blue-500",
    header: "bg-blue-50 border-blue-200 text-blue-700",
    badge: "bg-blue-50 text-blue-700 border-blue-200",
  },
];

const EMPTY_FORM = {
  client_id: "",
  client_name: "",
  platform: "instagram",
  caption: "",
  hashtags: [],
  media_url: "",
  scheduled_time: "",
  status: "draft",
  notes: "",
};

const AVATAR_COLORS = [
  "bg-blue-500",
  "bg-violet-500",
  "bg-emerald-500",
  "bg-rose-500",
  "bg-amber-500",
  "bg-cyan-500",
  "bg-pink-500",
  "bg-indigo-500",
];

function avatarColor(name = "") {
  let h = 0;
  for (let i = 0; i < name.length; i++)
    h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function getInitials(name = "") {
  return name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatDateTime(dt) {
  if (!dt) return null;
  const d = typeof dt === "string" ? new Date(dt) : dt;
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ── Hashtag Input ──────────────────────────────────────────────────────────

function HashtagInput({ value, onChange }) {
  const [input, setInput] = useState("");
  const inputRef = useRef(null);

  const add = (raw) => {
    const tags = raw
      .split(/[\s,]+/)
      .map((t) => t.replace(/^#+/, "").trim().toLowerCase())
      .filter(Boolean);
    const next = [...new Set([...value, ...tags])];
    onChange(next);
    setInput("");
  };

  const remove = (tag) => onChange(value.filter((t) => t !== tag));

  const handleKeyDown = (e) => {
    if (["Enter", ",", " "].includes(e.key)) {
      e.preventDefault();
      if (input.trim()) add(input);
    } else if (e.key === "Backspace" && !input && value.length > 0) {
      remove(value[value.length - 1]);
    }
  };

  return (
    <div
      className="min-h-10 flex flex-wrap gap-1.5 px-3 py-2 rounded-md border border-input bg-background cursor-text"
      onClick={() => inputRef.current?.focus()}
    >
      {value.map((tag) => (
        <span
          key={tag}
          className="flex items-center gap-1 text-xs bg-primary/10 text-primary border border-primary/20 rounded-full px-2 py-0.5 font-medium"
        >
          #{tag}
          <button
            type="button"
            onClick={() => remove(tag)}
            className="hover:text-red-500"
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (input.trim()) add(input);
        }}
        placeholder={
          value.length === 0 ? "Type a hashtag and press Enter…" : ""
        }
        className="flex-1 min-w-24 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
      />
    </div>
  );
}

// ── Post Form Dialog ───────────────────────────────────────────────────────

function PostFormDialog({
  open,
  onOpenChange,
  initial,
  clients,
  onSubmit,
  title,
  submitLabel,
}) {
  const [form, setForm] = useState(initial ?? EMPTY_FORM);

  useEffect(() => {
    setForm(initial ?? EMPTY_FORM);
  }, [initial, open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleClientChange = (e) => {
    const c = clients.find((cl) => cl.id === e.target.value);
    setForm((f) => ({
      ...f,
      client_id: c?.id ?? "",
      client_name: c?.name ?? "",
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.client_id) {
      toast.error("Please select a client");
      return;
    }
    if (!form.caption.trim()) {
      toast.error("Caption is required");
      return;
    }
    onSubmit(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Fill in the post details below.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          {/* Client */}
          <div>
            <Label>Client *</Label>
            <select
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm mt-1"
              value={form.client_id}
              onChange={handleClientChange}
              required
            >
              <option value="">Select a client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} — {c.business}
                </option>
              ))}
            </select>
          </div>

          {/* Platform (locked to Instagram for now) */}
          <div>
            <Label>Platform</Label>
            <div className="flex items-center gap-2 mt-1 h-10 px-3 rounded-md border border-input bg-muted/40 text-sm text-muted-foreground">
              {/* <Instagram className="w-4 h-4 text-pink-500" /> */}
              Instagram
            </div>
          </div>

          {/* Caption */}
          <div>
            <Label>Caption *</Label>
            <textarea
              className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm min-h-28 resize-y outline-none focus:ring-2 focus:ring-ring"
              placeholder="Write your caption here…"
              value={form.caption}
              onChange={set("caption")}
              required
            />
          </div>

          {/* Hashtags */}
          <div>
            <Label className="flex items-center gap-1.5">
              <Hash className="w-3.5 h-3.5" /> Hashtags
            </Label>
            <div className="mt-1">
              <HashtagInput
                value={form.hashtags}
                onChange={(v) => setForm((f) => ({ ...f, hashtags: v }))}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {form.hashtags.length} tag{form.hashtags.length !== 1 ? "s" : ""}{" "}
              added
            </p>
          </div>

          {/* Scheduled Time */}
          <div>
            <Label className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Scheduled Date & Time
            </Label>
            <div className="mt-1">
              <DateTimePicker
                value={form.scheduled_time}
                onChange={(val) => setForm((prev) => ({ ...prev, scheduled_time: val }))}
                placeholder="Pick publishing date and time"
              />
            </div>
          </div>

          {/* Media URL */}
          <div>
            <Label className="flex items-center gap-1.5">
              <LinkIcon className="w-3.5 h-3.5" /> Media URL (optional)
            </Label>
            <Input
              className="mt-1"
              placeholder="https://drive.google.com/…"
              value={form.media_url}
              onChange={set("media_url")}
            />
          </div>

          {/* Status */}
          <div>
            <Label>Status</Label>
            <select
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm mt-1"
              value={form.status}
              onChange={set("status")}
            >
              <option value="draft">Draft</option>
              <option value="approved">Approved</option>
              <option value="declined">Declined</option>
              <option value="scheduled">Scheduled</option>
              <option value="posted">Posted</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <Label className="flex items-center gap-1.5">
              <StickyNote className="w-3.5 h-3.5" /> Notes (optional)
            </Label>
            <Input
              className="mt-1"
              placeholder="Internal notes, approval comments…"
              value={form.notes}
              onChange={set("notes")}
            />
          </div>

          <Button type="submit" className="w-full">
            {submitLabel}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete Confirm ─────────────────────────────────────────────────────────

function DeleteConfirmDialog({ open, onOpenChange, onConfirm, loading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" /> Delete Post
          </DialogTitle>
          <DialogDescription>
            Are you sure? This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            className="flex-1"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? "Deleting…" : "Delete"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Post Card ──────────────────────────────────────────────────────────────

function PostCard({ post, column, onEdit, onDelete, onAdvance, onCopyReview, onPublishNow }) {
  const color = avatarColor(post.client_name);
  const initials = getInitials(post.client_name);

  return (
    <Card className="p-4 hover-lift group">
      {/* Client row */}
      <div className="flex items-center gap-2 mb-3">
        <div
          className={`w-7 h-7 rounded-lg ${color} text-white flex items-center justify-center text-xs font-bold shrink-0`}
        >
          {initials}
        </div>
        <span className="text-sm font-semibold truncate flex-1">
          {post.client_name}
        </span>
        {/* <Instagram className="w-4 h-4 text-pink-400 shrink-0" /> */}
      </div>

      {/* Caption */}
      <p className="text-sm text-foreground/80 leading-relaxed line-clamp-3 mb-3">
        {post.caption}
      </p>

      {/* Hashtags */}
      {post.hashtags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {post.hashtags.slice(0, 5).map((tag) => (
            <span
              key={tag}
              className="text-xs text-primary/70 bg-primary/8 border border-primary/15 rounded-full px-2 py-0.5"
            >
              #{tag}
            </span>
          ))}
          {post.hashtags.length > 5 && (
            <span className="text-xs text-muted-foreground px-1">
              +{post.hashtags.length - 5} more
            </span>
          )}
        </div>
      )}

      {/* Meta row */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground mb-3">
        {post.scheduled_time && (
          <span className="flex items-center gap-1">
            <CalendarDays className="w-3 h-3" />
            {formatDateTime(post.scheduled_time)}
          </span>
        )}
        {post.media_url && (
          <a
            href={post.media_url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 hover:text-primary transition-colors"
          >
            <LinkIcon className="w-3 h-3" /> Media
          </a>
        )}
        {post.notes && (
          <span className="flex items-center gap-1">
            <FileText className="w-3 h-3" />
            <span className="truncate max-w-32">{post.notes}</span>
          </span>
        )}
      </div>

      {/* Client Feedback Tag if present */}
      {post.client_feedback && (
        <div className="text-[11px] p-2 rounded bg-muted/60 border mb-2 text-foreground/80">
          <span className="font-semibold text-primary">Client Note:</span> {post.client_feedback}
        </div>
      )}

      {/* Review & Publish Row */}
      <div className="flex items-center gap-1.5 pb-2">
        <Button
          size="sm"
          variant="outline"
          className="text-[11px] h-6 flex-1 text-muted-foreground hover:text-foreground"
          onClick={() => onCopyReview(post)}
          title="Share secure review link with client"
        >
          <Copy className="w-3 h-3 mr-1" /> Client Link
        </Button>
        {(post.status === "approved" || post.status === "scheduled") && (
          <Button
            size="sm"
            variant="outline"
            className="text-[11px] h-6 border-pink-200 text-pink-700 hover:bg-pink-50"
            onClick={() => onPublishNow(post)}
            title="Publish directly to Instagram now"
          >
            <SendHorizontal className="w-3 h-3 mr-1" /> Publish
          </Button>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2 border-t border-border/50">
        {column.next && (
          <Button
            size="sm"
            variant="outline"
            className="text-xs flex-1 h-7"
            onClick={() => onAdvance(post, column.next)}
          >
            {column.nextLabel}
            <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        )}
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={() => onEdit(post)}
        >
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-muted-foreground hover:text-red-600 hover:bg-red-50"
          onClick={() => onDelete(post)}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </Card>
  );
}

// ── Skeleton ───────────────────────────────────────────────────────────────

function PostCardSkeleton() {
  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Skeleton className="w-7 h-7 rounded-lg" />
        <Skeleton className="h-4 flex-1" />
      </div>
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-4/5" />
      <Skeleton className="h-3 w-3/5" />
      <div className="flex gap-1">
        <Skeleton className="h-5 w-16 rounded-full" />
        <Skeleton className="h-5 w-20 rounded-full" />
      </div>
    </Card>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

export default function SocialPage() {

  const copyReviewLink = (post) => {
    if (!post.review_token) {
      toast.error("No review token generated for this post");
      return;
    }
    const origin = window.location.origin;
    const url = `${origin}/review/post/${post.review_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Client review link copied to clipboard!");
  };

  const handleManualPublish = async (post) => {
    try {
      toast.info("Publishing to Instagram...");
      await apiClient.post(`/social-posts/${post.id}/publish-now`);
      toast.success("Post successfully published to Instagram!");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to publish to Instagram");
    }
  };

  const [posts, setPosts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientFilter, setClientFilter] = useState("all");

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [postsRes, clientsRes] = await Promise.all([
        apiClient.get("/social-posts"),
        apiClient.get("/clients"),
      ]);
      setPosts(postsRes.data);
      setClients(clientsRes.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (formData) => {
    try {
      await apiClient.post("/social-posts", formData);
      toast.success("Post created");
      setCreateOpen(false);
      fetchAll();
    } catch {
      toast.error("Failed to create post");
    }
  };

  const handleEdit = async (formData) => {
    setEditLoading(true);
    try {
      await apiClient.patch(`/social-posts/${editTarget.id}`, formData);
      toast.success("Post updated");
      setEditTarget(null);
      fetchAll();
    } catch {
      toast.error("Failed to update post");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await apiClient.delete(`/social-posts/${deleteTarget.id}`);
      toast.success("Post deleted");
      setDeleteTarget(null);
      fetchAll();
    } catch {
      toast.error("Failed to delete post");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleAdvance = async (post, nextStatus) => {
    try {
      await apiClient.patch(`/social-posts/${post.id}`, { status: nextStatus });
      const col = COLUMNS.find((c) => c.key === nextStatus);
      toast.success(`Moved to ${col?.label}`);
      fetchAll();
    } catch {
      toast.error("Failed to update post");
    }
  };

  const filtered =
    clientFilter === "all"
      ? posts
      : posts.filter((p) => p.client_id === clientFilter);

  const byStatus = (status) => filtered.filter((p) => p.status === status);

  const activeClients = clients.filter((c) =>
    posts.some((p) => p.client_id === c.id),
  );

  return (
    <div className="space-y-6" data-testid="social-page">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1
            className="text-3xl lg:text-4xl font-bold"
            style={{ letterSpacing: "-0.02em" }}
          >
            Social Media
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {loading
              ? "Loading…"
              : `${posts.length} post${posts.length !== 1 ? "s" : ""} across ${clients.length} client${clients.length !== 1 ? "s" : ""}`}
          </p>
        </div>
        <Button
          className="w-full sm:w-auto"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          New Post
        </Button>
      </div>

      {/* ── Client Filter ── */}
      {!loading && activeClients.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setClientFilter("all")}
            className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${
              clientFilter === "all"
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
            }`}
          >
            All Clients
          </button>
          {activeClients.map((c) => (
            <button
              key={c.id}
              onClick={() => setClientFilter(c.id)}
              className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${
                clientFilter === c.id
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}

      {/* ── Kanban ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {COLUMNS.map((col) => {
          const colPosts = byStatus(col.key);
          return (
            <div key={col.key} className="flex flex-col gap-3">
              {/* Column header */}
              <div
                className={`flex items-center justify-between px-3 py-2 rounded-lg border ${col.header}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${col.dot}`} />
                  <span className="font-semibold text-sm">{col.label}</span>
                </div>
                <span className="text-xs font-bold bg-white/60 rounded-full px-2 py-0.5 border">
                  {loading ? "—" : colPosts.length}
                </span>
              </div>

              {/* Cards */}
              <div className="flex flex-col gap-3">
                {loading ? (
                  Array.from({ length: 2 }).map((_, i) => (
                    <PostCardSkeleton key={i} />
                  ))
                ) : colPosts.length === 0 ? (
                  <div className="py-10 text-center border border-dashed rounded-xl text-muted-foreground text-sm">
                    No {col.label.toLowerCase()} posts
                  </div>
                ) : (
                  colPosts.map((post) => (
                    <PostCard
                      key={post.id}
                      post={post}
                      column={col}
                      onEdit={setEditTarget}
                      onDelete={setDeleteTarget}
                      onAdvance={handleAdvance}
                      onCopyReview={copyReviewLink}
                      onPublishNow={handleManualPublish}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Dialogs ── */}
      <PostFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        initial={EMPTY_FORM}
        clients={clients}
        onSubmit={handleCreate}
        title="New Post"
        submitLabel="Create Post"
      />

      <PostFormDialog
        open={!!editTarget}
        onOpenChange={(v) => !v && setEditTarget(null)}
        initial={editTarget}
        clients={clients}
        onSubmit={handleEdit}
        title="Edit Post"
        submitLabel={editLoading ? "Saving…" : "Save Changes"}
      />

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleteLoading}
      />
    </div>
  );
}
